import {createAsyncThunk} from "@reduxjs/toolkit";
import {handlerError} from "../../../store/handleError";
import {thunkHandlers} from "../../../store/thunkHandlers";
import {AppDispatch, RootState} from "../../../store";
import {dispatchAPI} from "../api";
import {IBalancePair, IBalanceResult, IDispatchJob, IDispatchPoint, IDispatchRoute} from "../../../models/IDispatch";
import {setMessage} from "../../messages/model/slice";
import {MESSAGE_SEVERITY} from "../../../utils/const";

type ThunkConfig = {dispatch: AppDispatch; state: RootState; rejectValue: string};

const messages = {
    addPoint: {success: "Точка добавлена в справочник"},
    addJob: {success: "Заявка создана"},
    addRoute: {success: "Рейс создан"},
    approve: {success: "Рейс утверждён — виден водителю"},
    confirm: {success: "Закрытие подтверждено"},
    reject: {success: "Закрытие отклонено — заявка возвращена в работу"},
};

/** Единый показ ошибки в глобальный Message (по образцу machinery-actions). */
const reportError = (e: unknown, dispatch: AppDispatch, fallback: string) => {
    const text = e instanceof Error ? e.message : fallback;
    dispatch(setMessage({severity: MESSAGE_SEVERITY.error, text}));
};

/**
 * Обновить день целиком (заявки + рейсы) по выбранной в сторе дате.
 * Вызывается после каждой мутации и поллингом с доски: рейсовые мутации
 * задевают заявки (seq/status), проще и надёжнее перечитать день, чем
 * мержить точечно. Объём — десятки строк, не тысячи.
 */
export const fetchDispatchDay = createAsyncThunk<{jobs: IDispatchJob[]; routes: IDispatchRoute[]}, string | undefined, ThunkConfig>(
    "dispatching/fetch_day",
    async (dateArg, {getState, rejectWithValue}) => {
        try {
            const date = dateArg ?? getState().dispatching.selectedDate;
            const [jobs, routes] = await Promise.all([
                dispatchAPI.getJobs(date),
                dispatchAPI.getRoutes(date),
            ]);
            return {jobs, routes};
        } catch (e) {
            return rejectWithValue(handlerError(e));
        }
    },
);

// ── Справочник точек ────────────────────────────────────────────────────────

export const fetchGetDispatchPoints = createAsyncThunk<IDispatchPoint[], boolean | undefined, ThunkConfig>(
    "dispatching/get_points",
    async (includeArchived, {rejectWithValue}) => {
        try {
            return await dispatchAPI.getPoints(includeArchived ?? false);
        } catch (e) {
            return rejectWithValue(handlerError(e));
        }
    },
);

export const fetchAddDispatchPoint = createAsyncThunk<IDispatchPoint, Partial<IDispatchPoint>, ThunkConfig>(
    "dispatching/add_point",
    async (body, {dispatch, rejectWithValue}) => {
        try {
            const point = await dispatchAPI.addPoint(body);
            thunkHandlers.success(messages.addPoint.success, dispatch);
            return point;
        } catch (e) {
            reportError(e, dispatch, "Не удалось добавить точку.");
            return rejectWithValue(handlerError(e));
        }
    },
);

export const fetchUpdateDispatchPoint = createAsyncThunk<IDispatchPoint, {id: string; body: Partial<IDispatchPoint>}, ThunkConfig>(
    "dispatching/update_point",
    async ({id, body}, {dispatch, rejectWithValue}) => {
        try {
            return await dispatchAPI.updatePoint(id, body);
        } catch (e) {
            reportError(e, dispatch, "Не удалось обновить точку.");
            return rejectWithValue(handlerError(e));
        }
    },
);

export const fetchSetDispatchPointArchived = createAsyncThunk<IDispatchPoint, {id: string; archived: boolean}, ThunkConfig>(
    "dispatching/archive_point",
    async ({id, archived}, {rejectWithValue}) => {
        try {
            return await dispatchAPI.setPointArchived(id, archived);
        } catch (e) {
            return rejectWithValue(handlerError(e));
        }
    },
);

// ── Заявки ──────────────────────────────────────────────────────────────────

export const fetchAddDispatchJob = createAsyncThunk<IDispatchJob, Record<string, unknown>, ThunkConfig>(
    "dispatching/add_job",
    async (body, {dispatch, rejectWithValue}) => {
        try {
            const job = await dispatchAPI.addJob(body);
            thunkHandlers.success(messages.addJob.success, dispatch);
            return job;
        } catch (e) {
            reportError(e, dispatch, "Не удалось создать заявку.");
            return rejectWithValue(handlerError(e));
        }
    },
);

export const fetchUpdateDispatchJob = createAsyncThunk<IDispatchJob, {id: string; body: Record<string, unknown>}, ThunkConfig>(
    "dispatching/update_job",
    async ({id, body}, {dispatch, rejectWithValue}) => {
        try {
            return await dispatchAPI.updateJob(id, body);
        } catch (e) {
            reportError(e, dispatch, "Не удалось обновить заявку.");
            return rejectWithValue(handlerError(e));
        }
    },
);

/** Общая обёртка «действие над заявкой → перечитать день». */
const jobAction = (
    type: string,
    call: (id: string, extra?: string) => Promise<IDispatchJob>,
    successMessage?: string,
    fallbackError = "Не удалось выполнить действие.",
) =>
    createAsyncThunk<IDispatchJob, {id: string; extra?: string}, ThunkConfig>(
        type,
        async ({id, extra}, {dispatch, rejectWithValue}) => {
            try {
                const job = await call(id, extra);
                if (successMessage) thunkHandlers.success(successMessage, dispatch);
                void dispatch(fetchDispatchDay(undefined));
                return job;
            } catch (e) {
                reportError(e, dispatch, fallbackError);
                return rejectWithValue(handlerError(e));
            }
        },
    );

export const fetchSkipDispatchJob = jobAction("dispatching/skip_job", (id) => dispatchAPI.skipJob(id));
export const fetchCloseDispatchJob = jobAction("dispatching/close_job", (id, comment) => dispatchAPI.closeJob(id, comment));
export const fetchFailDispatchJob = jobAction("dispatching/fail_job", (id, reason) => dispatchAPI.failJob(id, reason ?? ""));
export const fetchConfirmDispatchJob = jobAction("dispatching/confirm_job", (id) => dispatchAPI.confirmJob(id), messages.confirm.success);
export const fetchRejectDispatchJob = jobAction("dispatching/reject_job", (id) => dispatchAPI.rejectJob(id), messages.reject.success);

export const fetchDeleteDispatchJob = createAsyncThunk<string, string, ThunkConfig>(
    "dispatching/delete_job",
    async (id, {dispatch, rejectWithValue}) => {
        try {
            await dispatchAPI.deleteJob(id);
            void dispatch(fetchDispatchDay(undefined));
            return id;
        } catch (e) {
            reportError(e, dispatch, "Не удалось удалить заявку.");
            return rejectWithValue(handlerError(e));
        }
    },
);

// ── Балансировщик ───────────────────────────────────────────────────────────

/**
 * Прогон распределения. Результат возвращается вызывающему (unwrap в
 * диалоге — там показываются причины unassigned), день перечитывается.
 */
export const fetchBalanceRun = createAsyncThunk<IBalanceResult, {date: string; pairs: IBalancePair[]; work_from?: string; work_to?: string; traffic_coef?: number}, ThunkConfig>(
    "dispatching/balance_run",
    async (body, {dispatch, rejectWithValue}) => {
        try {
            const result = await dispatchAPI.runBalance(body);
            void dispatch(fetchDispatchDay(body.date));
            return result;
        } catch (e) {
            reportError(e, dispatch, "Прогон распределения не выполнен.");
            return rejectWithValue(handlerError(e));
        }
    },
);

// ── Рейсы ───────────────────────────────────────────────────────────────────

export const fetchAddDispatchRoute = createAsyncThunk<IDispatchRoute, {date: string; machinery_id: string; driver_id: string; job_ids?: string[]}, ThunkConfig>(
    "dispatching/add_route",
    async (body, {dispatch, rejectWithValue}) => {
        try {
            const route = await dispatchAPI.addRoute(body);
            thunkHandlers.success(messages.addRoute.success, dispatch);
            void dispatch(fetchDispatchDay(undefined));
            return route;
        } catch (e) {
            reportError(e, dispatch, "Не удалось создать рейс.");
            return rejectWithValue(handlerError(e));
        }
    },
);

export const fetchUpdateDispatchRoute = createAsyncThunk<IDispatchRoute, {id: string; body: {machinery_id?: string; driver_id?: string; job_ids?: string[]}}, ThunkConfig>(
    "dispatching/update_route",
    async ({id, body}, {dispatch, rejectWithValue}) => {
        try {
            const route = await dispatchAPI.updateRoute(id, body);
            void dispatch(fetchDispatchDay(undefined));
            return route;
        } catch (e) {
            reportError(e, dispatch, "Не удалось изменить рейс.");
            return rejectWithValue(handlerError(e));
        }
    },
);

/** Общая обёртка «действие над рейсом → перечитать день». */
const routeAction = (
    type: string,
    call: (id: string) => Promise<IDispatchRoute>,
    successMessage?: string,
    fallbackError = "Не удалось выполнить действие.",
) =>
    createAsyncThunk<IDispatchRoute, string, ThunkConfig>(
        type,
        async (id, {dispatch, rejectWithValue}) => {
            try {
                const route = await call(id);
                if (successMessage) thunkHandlers.success(successMessage, dispatch);
                void dispatch(fetchDispatchDay(undefined));
                return route;
            } catch (e) {
                reportError(e, dispatch, fallbackError);
                return rejectWithValue(handlerError(e));
            }
        },
    );

export const fetchApproveDispatchRoute = routeAction("dispatching/approve_route", dispatchAPI.approveRoute, messages.approve.success);
export const fetchUnapproveDispatchRoute = routeAction("dispatching/unapprove_route", dispatchAPI.unapproveRoute);
export const fetchCancelDispatchRoute = routeAction("dispatching/cancel_route", dispatchAPI.cancelRoute);
export const fetchReconcileDispatchRoute = routeAction("dispatching/reconcile_route", dispatchAPI.reconcileRoute, "Пересчёт визитов выполнен");

export const fetchDeleteDispatchRoute = createAsyncThunk<string, string, ThunkConfig>(
    "dispatching/delete_route",
    async (id, {dispatch, rejectWithValue}) => {
        try {
            await dispatchAPI.deleteRoute(id);
            void dispatch(fetchDispatchDay(undefined));
            return id;
        } catch (e) {
            reportError(e, dispatch, "Не удалось удалить рейс.");
            return rejectWithValue(handlerError(e));
        }
    },
);
